import React from "react";
import {OverflowMenu as OctopusOverflowMenu, dividerMenuItem} from "@octopusdeploy/design-system-components";
import type {SimpleMenuItem} from "@octopusdeploy/design-system-components";
import {BaseComponent} from "components/BaseComponent/BaseComponent";
import {DoBusyTask} from "../DataBaseComponent/DataBaseComponent";
import OpenDeleteDialogMenuItem from "components/Menu/OpenDeleteDialogMenuItem";
import OpenDialogMenuItem from "components/Menu/OpenDialogMenuItem";
import {DialogSize} from "components/Dialog/Dialog";
import {resolvePathWithSpaceId} from "components/Navigation/resolvePathWithSpaceId";

enum OverflowMenuItemKind {
    Delete,
    Dialog,
    Generic,
    Disabled,
    Navigation,
    Download,
    Remove,
    ExternalNavigation
}

interface Item {
    kind: OverflowMenuItemKind;
}

export interface OverflowMenuDialogItem extends Item {
    text: string;
    dialogWidth?: DialogSize;
    child: JSX.Element;
}

export interface OverflowMenuDeleteItem extends Item {
    text: string;
    title: string;
    onClick: () => Promise<boolean>;
    content: ((doBusyTask: DoBusyTask) => React.ReactNode) | React.ReactNode;
    deleteButtonDisabled: boolean;
}

export interface OverflowMenuRemoveItem extends Item {
    text: string;
    title: string;
    onClick: () => Promise<boolean>;
    content: React.ReactNode;
    removeButtonDisabled: boolean;
}

export interface OverflowMenuNavLink extends Item {
    text: string;
    path: string;
    newTab: boolean;
    queryString?: string;
}

export interface OverflowMenuDownloadItem extends Item {
    text: string;
    link: string;
    filename: string;
}

export interface OverflowMenuDisabledItem extends Item {
    text: string;
    reason: string;
}

export interface OverflowMenuGenericItem extends Item {
    text: string;
    onClick: () => void;
}

function isGroup(item: MenuItem | MenuItem[]): item is MenuItem[] {
    return Array.isArray(item as MenuItem[]);
}

function isOverflowMenuDialogItem(item: MenuItem): item is OverflowMenuDialogItem {
    return (item as OverflowMenuDialogItem).kind === OverflowMenuItemKind.Dialog;
}

function isOverflowMenuDeleteItem(item: MenuItem): item is OverflowMenuDeleteItem {
    return (item as OverflowMenuDeleteItem).kind === OverflowMenuItemKind.Delete;
}

function isOverflowMenuRemoveItem(item: MenuItem): item is OverflowMenuRemoveItem {
    return (item as OverflowMenuRemoveItem).kind === OverflowMenuItemKind.Remove;
}

function isOverflowMenuDownloadItem(item: MenuItem): item is OverflowMenuDownloadItem {
    return (item as OverflowMenuDownloadItem).kind === OverflowMenuItemKind.Download;
}

function isOverflowMenuNavLink(item: MenuItem): item is OverflowMenuNavLink {
    return (item as OverflowMenuNavLink).kind === OverflowMenuItemKind.Navigation;
}

function isOverflowMenuExternalNavLink(item: MenuItem): item is OverflowMenuNavLink {
    return (item as OverflowMenuNavLink).kind === OverflowMenuItemKind.ExternalNavigation;
}

function isOverflowMenuDisabledItem(item: MenuItem): item is OverflowMenuDisabledItem {
    return (item as OverflowMenuDisabledItem).kind === OverflowMenuItemKind.Disabled;
}

export type MenuItem =
    OverflowMenuDialogItem
    | OverflowMenuDeleteItem
    | OverflowMenuNavLink
    | OverflowMenuDownloadItem
    | OverflowMenuGenericItem
    | OverflowMenuDisabledItem
    | OverflowMenuRemoveItem;

interface OverflowMenuProps {
    menuItems: Array<MenuItem | MenuItem[]>;
    tabIndex?: number;
    menuKey?: string;
    colorOverride?: string;
}

class OverflowMenu extends BaseComponent<OverflowMenuProps, any> {
    private dialogRefs = new Map<string, any>();

    static dialogItem(text: string, child: JSX.Element, dialogWidth?: DialogSize): OverflowMenuDialogItem {
        return {text, child, dialogWidth, kind: OverflowMenuItemKind.Dialog};
    }

    static removeItem(text: string, title: string, onClick: () => Promise<boolean>, content: React.ReactNode, removeButtonDisabled: boolean = false): OverflowMenuRemoveItem {
        return {text, title, onClick, content, kind: OverflowMenuItemKind.Remove, removeButtonDisabled};
    }

    static deleteItem(text: string,
                      title: string,
                      onClick: () => Promise<boolean>,
                      content: ((doBusyTask: DoBusyTask) => React.ReactNode) | React.ReactNode,
                      deleteButtonDisabled: boolean = false): OverflowMenuDeleteItem {
        return {text, title, onClick, content, kind: OverflowMenuItemKind.Delete, deleteButtonDisabled};
    }

    static deleteItemDefault(name: string, onClick: () => Promise<boolean>, customMessage?: string, customContent?: JSX.Element, deleteButtonDisabled: boolean = false): OverflowMenuDeleteItem {
        return {
            text: "Delete",
            title: `Are you sure you want to delete this ${name}?`,
            onClick,
            kind: OverflowMenuItemKind.Delete,
            deleteButtonDisabled,
            content: <div>
                <p>Deleting this {name} is permanent, there is no going back.</p>
                {customMessage && <p>{customMessage}</p>}
                {customContent}
                <p>Do you wish to continue?</p>
            </div>
        };
    }

    static navItem(text: string, path: string, queryString?: string): OverflowMenuNavLink {
        return {text, path, queryString, kind: OverflowMenuItemKind.Navigation, newTab: false};
    }

    static externalNavItem(text: string, path: string, queryString?: string): OverflowMenuNavLink {
        return {text, path, queryString, kind: OverflowMenuItemKind.ExternalNavigation, newTab: true};
    }

    static disabledItem(text: string, reason: string): OverflowMenuDisabledItem {
        return {text, reason, kind: OverflowMenuItemKind.Disabled};
    }

    static item(text: string, onClick: () => void): OverflowMenuGenericItem {
        return {text, onClick, kind: OverflowMenuItemKind.Generic};
    }

    static downloadItem(text: string, filename: string, link: string) {
        return {text, link, filename, kind: OverflowMenuItemKind.Download};
    }

    private openDialog(key: string) {
        const ref = this.dialogRefs.get(key);
        if (ref) {
            if (typeof ref.onClick === "function") {
                ref.onClick();
            } else if (typeof ref.getWrappedInstance === "function") {
                ref.getWrappedInstance().onClick();
            }
        }
    }

    private convertItem(item: MenuItem): {simpleItem: SimpleMenuItem | null; dialog?: React.ReactNode} {
        const key = `${this.props.menuKey || ""}-${item.text}`;

        if (isOverflowMenuDialogItem(item)) {
            const dialog = <OpenDialogMenuItem
                ref={(d: any) => { if (d) this.dialogRefs.set(key, d); }}
                label={item.text}
                key={key}
                dialogWidth={item.dialogWidth}
            >
                {item.child}
            </OpenDialogMenuItem>;
            return {
                simpleItem: {type: "button", label: item.text, onClick: () => this.openDialog(key)},
                dialog,
            };
        }

        if (isOverflowMenuDeleteItem(item)) {
            const dialog = <OpenDeleteDialogMenuItem
                ref={(d: any) => { if (d) this.dialogRefs.set(key, d); }}
                deleteButtonLabel={item.text}
                disabled={false}
                deleteButtonDisabled={item.deleteButtonDisabled}
                dialogTitle={item.title}
                key={key}
                onDeleteClick={item.onClick}
                renderContent={(doBusyTask: DoBusyTask) => {
                    if (typeof (item.content) === "function") {
                        return item.content(doBusyTask);
                    }
                    return item.content;
                }}
            />;
            return {
                simpleItem: {type: "button", label: item.text, onClick: () => this.openDialog(key)},
                dialog,
            };
        }

        if (isOverflowMenuRemoveItem(item)) {
            const dialog = <OpenDeleteDialogMenuItem
                ref={(d: any) => { if (d) this.dialogRefs.set(key, d); }}
                label={item.text}
                disabled={false}
                deleteButtonDisabled={item.removeButtonDisabled}
                dialogTitle={item.title}
                deleteButtonLabel="Remove"
                deleteButtonBusyLabel="Removing"
                key={key}
                onDeleteClick={item.onClick}
                renderContent={() => item.content}
            />;
            return {
                simpleItem: {type: "button", label: item.text, onClick: () => this.openDialog(key)},
                dialog,
            };
        }

        if (isOverflowMenuDownloadItem(item)) {
            return {
                simpleItem: {
                    type: "download-link",
                    label: item.text,
                    href: item.link,
                    downloadFileName: item.filename,
                },
            };
        }

        if (isOverflowMenuNavLink(item)) {
            const href = item.queryString ? `${item.path}?${item.queryString}` : item.path;
            // internal navigation — resolve with space id if needed
            const resolved = resolvePathWithSpaceId(href, undefined) as string;
            return {
                simpleItem: {
                    type: "internal-link",
                    label: item.text,
                    path: resolved as any,
                },
            };
        }

        if (isOverflowMenuExternalNavLink(item)) {
            const href = item.queryString ? `${item.path}?${item.queryString}` : item.path;
            return {
                simpleItem: {
                    type: "external-link",
                    label: item.text,
                    href,
                },
            };
        }

        if (isOverflowMenuDisabledItem(item)) {
            return {
                simpleItem: {
                    type: "disabled",
                    label: item.text,
                    disableReason: item.reason,
                },
            };
        }

        // Generic item
        return {
            simpleItem: {
                type: "button",
                label: item.text,
                onClick: item.onClick,
            },
        };
    }

    private iconMenuOnClickOverride = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
    };

    render() {
        if (!this.props.menuItems || this.props.menuItems.length === 0) {
            return null;
        }

        const simpleItems: SimpleMenuItem[] = [];
        const dialogs: React.ReactNode[] = [];

        const filteredItems = this.props.menuItems.filter(item => !!item);

        filteredItems.forEach((item, index) => {
            if (isGroup(item)) {
                const groupItems = item.filter(subItem => !!subItem);
                if (groupItems.length === 0) return;

                // Divider before this group if the prior item wasn't a group
                if (index > 0 && !isGroup(filteredItems[index - 1])) {
                    simpleItems.push(dividerMenuItem);
                }

                groupItems.forEach(subItem => {
                    const {simpleItem, dialog} = this.convertItem(subItem);
                    if (simpleItem) simpleItems.push(simpleItem);
                    if (dialog) dialogs.push(dialog);
                });

                // Divider after this group if it's not the last group
                if (index < filteredItems.length - 1) {
                    simpleItems.push(dividerMenuItem);
                }
            } else {
                const {simpleItem, dialog} = this.convertItem(item);
                if (simpleItem) simpleItems.push(simpleItem);
                if (dialog) dialogs.push(dialog);
            }
        });

        if (simpleItems.length === 0) return null;

        return <div onClick={this.iconMenuOnClickOverride}>
            {dialogs}
            <OctopusOverflowMenu menuItems={simpleItems} />
        </div>;
    }
}

export default OverflowMenu;
